// This component is responsible for rendering cells in Sheets and Resource Tables
import React from "react"
import { connect } from "react-redux"
import classNames from "classnames"
import isEqual from "lodash.isequal"

// components
import ErrorCell from "app/static/frontend/sheets/components/cells/ErrorCell"
import EditableIcon from "app/static/frontend/sheets/components/EditableIcon"
import SegueLink from "app/static/frontend/middleware/SegueLink"

// selectors
import {
    cellConnection,
    mantineCellConnection,
    mantineGroupedCellConnection,
} from "app/static/frontend/sheets/selectors/cell-component-selectors"

// action creators
import { clickCell } from "app/static/frontend/sheets/sheet-action-creators"

// helpers
import { isObject } from "shared/imports/sharedHelperFunctions"

// this is a wrapper component that encapsulates the logic of figuring out
// what type of Cell to render as well as what to render in each cell of a Sheet
export class SheetCell extends React.Component {
    // due to the .toJS() calls in cellConnection,
    // we will update/re-render every SheetCell currently rendered from Sheet.jsx
    // every time we update the redux store

    // this occurs because every .toJS() call creates a new object prototype,
    // which means that the React reconciler updates the component every single time we
    // invoke our selector logic (even if no changes have occurred in the values being selected)

    // this can lead to a catatrophic decline in performance,
    // particularly when we update redux store values onScroll()

    // TODO: remove all Immutable .toJS() function calls in
    // app/static/frontend/sheets/selectors/column-selectors,
    // convert SheetCell to React.PureComponent,
    // delete shouldComponentUpdate,
    // and wrap the exported/connected SheetCell with withImmutablePropsToJS
    // or look into https://github.com/immerjs/immer
    shouldComponentUpdate(nextProps) {
        return (
            this.props.cellState !== nextProps.cellState ||
            this.props.editing !== nextProps.editing ||
            this.props.height !== nextProps.height ||
            this.props.rowHeightFactor !== nextProps.rowHeightFactor ||
            this.props.rowId !== nextProps.rowId ||
            this.props.width !== nextProps.width ||
            this.props.isClickedCell !== nextProps.isClickedCell ||
            this.props.columnIndex !== nextProps.columnIndex ||
            (isObject(this.props.cellValue)
                ? !isEqual(this.props.cellValue, nextProps.cellValue)
                : this.props.cellValue !== nextProps.cellValue)
        )
    }

    render() {
        const {
            CellComponent,
            cellComponentClassName,
            cellLink,
            cellIsEditable,
            cellState,
            cellStyle,
            cellValue,
            className,
            columnCount,
            columnId,
            columnIndex,
            columnIsEditable,
            dataType,
            derived,
            editing,
            field,
            isClickedCell,
            isCustomDataSheet,
            isMantineSheet,
            isPublicSheet,
            isResourceTable,
            menuShouldScrollIntoView,
            rowHeightFactor,
            row,
            rowId,
            rowIndex,
            sheetDataType,
            sheetKey,
            shouldRoundCellValue,
            shouldShowEditableIcon,
            isGrouped,
            groupedAmount,
            ...props
        } = this.props

        return (
            <SegueLink to={cellLink}>
                <div
                    className={classNames({
                        "clicked-cell": isClickedCell,
                        editable: columnIsEditable && cellIsEditable,
                    })}
                    data-cy={columnIsEditable && cellIsEditable ? "cy-editable-cell" : "cy-cell"}
                    // when clicked, call the action creator attached to the sheet with
                    // the necessary identifying information.
                    onClick={(e) => this.props.clickCell(sheetKey, rowId, columnId, e)}
                    onKeyUp={(e) => this.props.clickCell(sheetKey, rowId, columnId, e)}
                    tabIndex={0}
                    role="button"
                    style={
                        isMantineSheet && {
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "stretch",
                            ...(columnIsEditable && cellIsEditable ? { cursor: "pointer" } : { cursor: "default" }),
                        }
                    }
                >
                    <CellComponent
                        cellValue={cellValue}
                        cellIsEditable={cellIsEditable}
                        className={cellComponentClassName}
                        columnCount={columnCount}
                        columnId={columnId}
                        columnIndex={columnIndex}
                        columnIsEditable={columnIsEditable}
                        data-interactive={columnIsEditable}
                        derived={derived}
                        editing={editing}
                        field={field}
                        isCustomDataSheet={isCustomDataSheet}
                        isPublicSheet={isPublicSheet}
                        isResourceTable={isResourceTable}
                        menuShouldScrollIntoView={menuShouldScrollIntoView}
                        rowHeightFactor={rowHeightFactor}
                        row={row}
                        rowId={rowId}
                        sheetDataType={dataType}
                        sheetId={this.props.id}
                        sheetKey={this.props.id}
                        style={cellStyle}
                        isGrouped={isGrouped}
                        groupedAmount={groupedAmount}
                        {...props}
                    >
                        {cellValue}
                    </CellComponent>
                    {cellState === "error" && <ErrorCell />}
                    {shouldShowEditableIcon && <EditableIcon />}
                </div>
            </SegueLink>
        )
    }
}

SheetCell.defaultProps = {
    columnIsEditable: false,
    menuShouldScrollIntoView: false,
}

export const MantineSheetCell = connect((state, props) => mantineCellConnection(state, props), { clickCell })(SheetCell)

export const MantineGroupedSheetCell = connect((state, props) => mantineGroupedCellConnection(state, props), {
    clickCell,
})(SheetCell)

export default connect((state, props) => cellConnection(state, props), { clickCell })(SheetCell)
